import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

import { mapSquadToTeam } from '../shared/map-squad';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { importTeamFromScreenshot as importScreenshot } from '@lib/api/import-team';
import { fetchSquadPlayers } from '@lib/api/gytr-portraits';
import { reportFunnelCompletion } from '@lib/api/funnel-email-capture';
import {
  buildFunnelCompletedSnapshot,
  getFunnelDimensions
} from '@lib/funnels/funnel-dimensions';
import { ClevertapEventProperties } from '@lib/clevertap';
import { fireLeadEvent } from '@lib/analytics/fireLead';
import { AnalyticsEvents, captureAnalyticsEvent } from '@lib/analytics/events';
import {
  collectTransferSuggestion,
  fetchTeamRatingByFplId,
  fetchTeamRatingByPlayerIds,
  readFailureKind,
  readSuggestedTransfers,
  TeamRatingError
} from '@lib/api/team-rating';
import { datadogRumService } from '@lib/datadogRum';
import type { RateTeamData } from '@lib/api/team-rating';
import {
  GytrCalculationFailure,
  GytrContextValue,
  GytrTeamMember,
  INITIAL_GYTR_STATE,
  GytrState,
  ScreenshotImportOutcome,
  ScreenshotPlayer
} from 'types/gytr';
import { assessScreenshotSquad } from '@lib/quiz/screenshot-squad';

const GytrContext = createContext<GytrContextValue | null>(null);

const TEAM_RATING_ENDPOINT = '/team-rating';
const RATING_FAILURE_CONTEXT = 'team-rating-calculation';

/**
 * How much of the RUM session id to show. Six characters is enough for support
 * to match a report to a session and short enough to read down a phone.
 */
const ERROR_REFERENCE_LENGTH = 6;

const DEFAULT_FAILURE_MESSAGE =
  'Something went wrong analysing your team. Please try again.';

export const useGytr = (): GytrContextValue => {
  const context = useContext(GytrContext);

  if (!context) {
    throw new Error('useGytr must be used within a GytrProvider');
  }

  return context;
};

type GytrProviderProps = {
  children: ReactNode;
  templateKey: string;
};

const GytrProvider: FC<GytrProviderProps> = ({ children, templateKey }) => {
  const [state, setState] = useState<GytrState>(INITIAL_GYTR_STATE);

  const { context } = useLeagueContext();
  const nextGameweek = context?.nextGameweek?.number ?? null;

  const isPreGw1 = templateKey === 'gytr-pre-gw1';

  /**
   * Cancels the wait for a suggestion belonging to a rating nobody is looking
   * at any more: a re-rate, a restart, or the funnel being left.
   */
  const suggestionPollRef = useRef<AbortController | null>(null);

  /**
   * Which rating the funnel is currently showing.
   *
   * A suggestion outlives the request that deferred it, so a slow solve can
   * land after the visitor has already re-rated a different squad. Aborting
   * covers the poll still running, but not one whose final read has already
   * come back, so the arriving suggestion is checked against this before it is
   * allowed to touch state.
   */
  const ratingRequestRef = useRef(0);

  const abandonSuggestionPoll = useCallback(() => {
    suggestionPollRef.current?.abort();
    suggestionPollRef.current = null;
  }, []);

  // Leaving the funnel ends the wait. Nothing downstream can consume it, and a
  // poll left running would keep asking for minutes after the page has gone.
  useEffect(() => abandonSuggestionPoll, [abandonSuggestionPoll]);

  const importTeamFromScreenshotAction = useCallback(
    async (
      file: File,
      readErrorText?: string
    ): Promise<ScreenshotImportOutcome> => {
      setState((previous) => ({
        ...previous,
        isImporting: true,
        importError: null,
        calculationFailure: null
      }));

      try {
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onloadend = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const result = await importScreenshot(base64);
        const players = result.team.players;

        // A read the rules refuse is not a failed read: everything needed to
        // put the squad on a pitch is in hand, so it goes to the review step
        // instead of an error the visitor cannot act on (FFH-2738). Checked
        // here rather than at rating time so the correction happens before the
        // email gate, two steps earlier than the API's refusal would surface.
        if (!assessScreenshotSquad(players).isComplete) {
          setState((previous) => ({
            ...previous,
            team: [],
            // Nothing is rateable until the review is confirmed, and clearing
            // both keeps a squad from an earlier import in this session out of
            // the rating request.
            teamSource: null,
            entryMethod: null,
            playerUuids: null,
            screenshotMatches: players,
            isReviewingScreenshot: true,
            isImporting: false
          }));
          return 'needs-review';
        }

        const playerUuids: ScreenshotPlayer[] = players.map((match) => ({
          id: match.player.id,
          displayName: match.player.displayName
        }));

        setState((previous) => ({
          ...previous,
          team: [],
          teamSource: 'screenshot',
          // The import chose these players, so it owns the entry method.
          entryMethod: 'SCREENSHOT',
          playerUuids,
          screenshotMatches: players,
          isReviewingScreenshot: false,
          isImporting: false
        }));
        return 'imported';
      } catch {
        setState((previous) => ({
          ...previous,
          isImporting: false,
          importError:
            readErrorText ??
            "We couldn't read your team from that screenshot. Please try again or enter your FPL ID instead."
        }));
        return 'error';
      }
    },
    []
  );

  const importTeamByFplId = useCallback(async (fplId: string) => {
    setState((previous) => ({
      ...previous,
      isImporting: false,
      importError: null,
      calculationFailure: null,
      fplId,
      teamSource: 'fplId',
      // The backend fetches this squad from FPL, so there is no client-side
      // entry method to report.
      entryMethod: null
    }));
  }, []);

  /**
   * The squad the visitor confirmed on the review pitch.
   *
   * Keeps SCREENSHOT: the import chose these players and the visitor only
   * corrected what the parser got wrong, so attributing the squad to the picker
   * would hide every OCR miss from the entry-method breakdown. See the
   * TeamEntryMethod docs in lib/api/team-rating.
   */
  const setTeamFromReview = useCallback((team: GytrTeamMember[]) => {
    setState((previous) => ({
      ...previous,
      team,
      teamSource: 'manual',
      entryMethod: 'SCREENSHOT',
      isReviewingScreenshot: false,
      importError: null,
      calculationFailure: null
    }));
  }, []);

  const setIsReviewingScreenshot = useCallback((isReviewing: boolean) => {
    setState((previous) => ({
      ...previous,
      isReviewingScreenshot: isReviewing
    }));
  }, []);

  const setTeamManually = useCallback((team: GytrTeamMember[]) => {
    setState((previous) => ({
      ...previous,
      team,
      teamSource: 'manual',
      // PICKER even when a screenshot was imported earlier in the session: the
      // picker starts empty, so a hand-built squad is a fresh assembly that
      // shares nothing with the abandoned import, and attributing it to the
      // import would blame OCR for players the member chose themselves. See the
      // TeamEntryMethod docs for the seeded-picker case.
      entryMethod: 'PICKER',
      importError: null
    }));
  }, []);

  /**
   * Records a rating that did not come back, on both rails: Datadog RUM for
   * diagnostics (always on) and PostHog for the product funnel (consent-gated),
   * mirroring what screenshot import has always done in lib/api/import-team.
   *
   * Every attempt is reported, retries included, so a session that failed
   * repeatedly is visible as one rather than as a single blip.
   */
  const reportRatingFailure = useCallback(
    (error: unknown, attempt: number): GytrCalculationFailure => {
      const kind = readFailureKind(error);
      const status = error instanceof TeamRatingError ? error.status : null;
      const message =
        error instanceof Error && error.message
          ? error.message
          : DEFAULT_FAILURE_MESSAGE;

      captureAnalyticsEvent(AnalyticsEvents.teamRatingFailed, {
        error_message: message,
        failure_kind: kind,
        status,
        attempt
      });

      datadogRumService.addError(
        error instanceof Error ? error : new Error(String(error)),
        {
          context: RATING_FAILURE_CONTEXT,
          endpoint: TEAM_RATING_ENDPOINT,
          failureMode: kind,
          status,
          attempt,
          entryMethod: state.entryMethod,
          templateKey
        }
      );

      // Read after reporting: the session exists by now even if this is the
      // first thing RUM has been given.
      const sessionId = datadogRumService.getSessionId();

      return {
        message,
        kind,
        status,
        reference: sessionId ? sessionId.slice(0, ERROR_REFERENCE_LENGTH) : null
      };
    },
    [state.entryMethod, templateKey]
  );

  const calculateRating = useCallback(
    async (attempt = 1) => {
      // Any suggestion still being waited for belongs to the previous rating.
      abandonSuggestionPoll();
      const ratingRequestId = ratingRequestRef.current + 1;
      ratingRequestRef.current = ratingRequestId;

      setState((previous) => ({
        ...previous,
        isCalculating: true,
        calculationFailure: null,
        teamRating: null,
        potentialRating: null,
        topTransfers: [],
        topTransfer: null,
        isSuggestionPending: false,
        playerPortraits: null
      }));

      try {
        let result: RateTeamData;

        if (state.teamSource === 'fplId' && state.fplId) {
          const fplId = parseInt(state.fplId, 10);
          if (Number.isNaN(fplId)) {
            throw new Error('Please enter a valid FPL ID (numbers only).');
          }
          result = await fetchTeamRatingByFplId(fplId);
        } else if (state.teamSource === 'screenshot' && state.playerUuids) {
          // No captain flags — the server picks captain/vice from predictions.
          result = await fetchTeamRatingByPlayerIds(
            state.playerUuids.map((p) => ({ id: p.id })),
            state.entryMethod ?? undefined
          );
        } else if (state.teamSource === 'manual') {
          // Slot order is XI-first then bench, so submit in order; the server
          // picks the captain from the highest-predicted-points starter.
          result = await fetchTeamRatingByPlayerIds(
            state.team
              .map((member) => member.player?.id)
              .filter((id): id is string => Boolean(id))
              .map((id) => ({ id })),
            state.entryMethod ?? undefined
          );
        } else {
          throw new Error('No team source available');
        }

        // A rating the funnel has already moved on from, arriving late. Dropped
        // before it can write anything: it would otherwise replace a newer squad
        // with an older one, and a stale SUGGESTION_PENDING would set a flag that
        // only its own collection could clear, which the same check below
        // correctly refuses to let it do. It must not report itself either, or a
        // superseded rating is measured as a completion of its own.
        if (ratingRequestRef.current !== ratingRequestId) return;

        const team = mapSquadToTeam(result.squad);

        const gw1Performance =
          !isPreGw1 && result.performance
            ? {
                captainActualPoints:
                  result.performance.captain.captain.actualPoints,
                optimalCaptainPoints:
                  result.performance.captain.bestPlayer.actualPoints,
                optimalCaptainName:
                  result.performance.captain.bestPlayer.displayName,
                missingHighOwnershipPlayer:
                  result.performance.ownership?.player.displayName ?? '',
                missingPlayerOwnership: Math.round(
                  result.performance.ownership?.player.selectedByPercent ?? 0
                ),
                missingPlayerGw1Points:
                  result.performance.ownership?.player.actualPoints ?? 0,
                squadGameweekPoints: result.performance.squadGameweekPoints,
                percentile: result.performance.rank?.percentile ?? null
              }
            : null;

        // The solve was queued rather than answered, so the improvement step has
        // to wait for it. Both halves are required, and the handle is the half
        // that decides: an outcome claiming to be pending without one names
        // nothing to wait on, and treating it as pending anyway would leave the
        // step waiting on a poll that was never started. Read defensively for the
        // same reason `hasWeakStarter` is — a response predating either field
        // should read as the settled case, not the stuck one.
        const suggestionRun =
          result.transferSuggestion?.outcome === 'SUGGESTION_PENDING'
            ? result.suggestionRun ?? null
            : null;

        // Both fields come off the one list, so `topTransfer` cannot disagree
        // with the head of `topTransfers` the way it would if each read the
        // response separately. The API does keep them consistent — an empty
        // `transfers` always arrives with a null `transfer` — but relying on that
        // would let a present-but-empty list sit beside a stale single transfer
        // for anything still reading the singular field.
        const topTransfers = readSuggestedTransfers(
          result.transferSuggestion,
          result.topTransfer
        );

        setState((previous) => ({
          ...previous,
          team,
          optimumPoints: result.optimumPoints,
          teamRating: result.rating,
          potentialRating: result.potentialRating,
          topTransfers,
          topTransfer: topTransfers[0] ?? null,
          isSuggestionPending: suggestionRun !== null,
          aiSellPlayerId: result.aiSellPlayerId,
          // Default false if the field is absent (e.g. this deploys before the
          // backend, or an older response is cached): no flag reads as no weak
          // link, the safe neutral fallback.
          hasWeakStarter: result.hasWeakStarter ?? false,
          gw1Performance,
          isCalculating: false
        }));

        // Wait for the deferred suggestion alongside the rest of the funnel
        // rather than in front of it. Not awaited: the rating above is final and
        // the visitor is already moving through the reveal, so the transfer step
        // is the only thing this can still change.
        if (suggestionRun) {
          const controller = new AbortController();
          suggestionPollRef.current = controller;

          collectTransferSuggestion({
            ...suggestionRun,
            signal: controller.signal
          })
            .then((collected) => {
              // A suggestion for a rating that has since been replaced is not
              // wrong, just answering a question nobody is asking any more.
              if (ratingRequestRef.current !== ratingRequestId) return;

              setState((previous) => ({
                ...previous,
                isSuggestionPending: false,
                // Left alone unless there is something to put there, so a solve
                // that named no move renders as one that never had one.
                ...(collected.status === 'suggested' && {
                  topTransfers: collected.topTransfers,
                  topTransfer: collected.topTransfer,
                  potentialRating: collected.potentialRating
                })
              }));
            })
            // Abandoned, which is the one ending with nothing to record: the
            // state it would have written belongs to a rating already gone.
            .catch(() => undefined)
            .finally(() => {
              // Only if this is still the poll the ref describes. A finished poll
              // clearing the ref unconditionally would drop a newer controller
              // put there by a rating that started while this one was settling,
              // and that controller is the only handle for cancelling it.
              if (suggestionPollRef.current === controller) {
                suggestionPollRef.current = null;
              }
            });
        }

        // Prefetch the squad portraits so the reveal pitch has no loading flash.
        // Fire-and-forget: failure just falls back to a per-step fetch.
        const squadUuids = team
          .map((member) => member.player?.id)
          .filter((id): id is string => Boolean(id));
        fetchSquadPlayers(squadUuids)
          .then((portraits) =>
            setState((previous) => ({
              ...previous,
              playerPortraits: portraits
            }))
          )
          .catch(() => undefined);

        // Spec §4.2 GYTR properties. Captain match/cost are post-GW1 only: the
        // rating API returns performance data only after GW1, and pre-GW1 the
        // server auto-picks the captain so there is no user pick to compare.
        //
        // A deferred suggestion is reported without the potential rating or the
        // uplift, because neither exists yet. They are not sent on afterwards
        // either: the snapshot PATCH re-fires sanity_funnel_completed and
        // re-stamps its completion date on every call, so a second, fuller
        // submission would buy two properties at the cost of double-counting the
        // completion itself. The snapshot is instead marked as still waiting, and
        // the backend holds the completion until the suggestion lands and can
        // report it in full (ffh-public-api#569).
        const captain = result.performance?.captain;
        const captainMatchesAi = captain
          ? captain.captain.playerId === captain.bestPlayer.playerId
          : null;
        const completedProps = {
          [ClevertapEventProperties.gytrScore]: result.rating,
          ...(result.potentialRating != null && {
            [ClevertapEventProperties.gytrScorePotential]:
              result.potentialRating
          }),
          ...(result.topTransfer != null && {
            [ClevertapEventProperties.gytrTransferUplift]:
              result.topTransfer.pointsUplift
          }),
          ...(captain != null && {
            [ClevertapEventProperties.gytrCaptainMatchAi]: captainMatchesAi,
            [ClevertapEventProperties.gytrCaptainCost]: captainMatchesAi
              ? 0
              : captain.bestPlayer.actualPoints - captain.captain.actualPoints
          })
        };

        // The backend fires the CleverTap completion event on this same
        // payload — see ffh-public-api's funnel-completed-uploader.ts.
        const resultSnapshot = buildFunnelCompletedSnapshot(
          getFunnelDimensions(templateKey),
          completedProps
        );
        reportFunnelCompletion({
          funnelSlug: templateKey,
          resultSnapshot,
          email: state.capturedEmail ?? undefined,
          isCompletionDeferred: suggestionRun !== null
        });

        // FFH-990: Lead on both rails (GTM pixel + CAPI), Pattern A shared id.
        fireLeadEvent('team_rating', String(result.rating));

        // FFH-2372: the product analytics sibling of the Lead above, fired
        // where the rating is in hand rather than in the reveal component. A
        // deferred solve means the potential honestly does not exist yet, so
        // it goes out null rather than waiting (see the snapshot rationale
        // above) or guessing.
        captureAnalyticsEvent(AnalyticsEvents.teamRatingViewed, {
          rating: result.rating,
          rating_potential: result.potentialRating ?? null
        });
      } catch (error) {
        setState((previous) => ({
          ...previous,
          isCalculating: false,
          calculationFailure: reportRatingFailure(error, attempt)
        }));
      }
    },
    [
      state.team,
      state.teamSource,
      state.entryMethod,
      state.fplId,
      state.playerUuids,
      state.capturedEmail,
      isPreGw1,
      templateKey,
      abandonSuggestionPoll,
      reportRatingFailure
    ]
  );

  const setCapturedEmail = useCallback((email: string) => {
    setState((previous) => ({ ...previous, capturedEmail: email }));
  }, []);

  const resetImportError = useCallback(() => {
    setState((previous) => ({ ...previous, importError: null }));
  }, []);

  const resetForRetry = useCallback(() => {
    // Starting the funnel over abandons the suggestion with it, and moves the
    // rating on so a read already in flight cannot write to the cleared state.
    abandonSuggestionPoll();
    ratingRequestRef.current += 1;

    setState((previous) => ({
      ...previous,
      team: [],
      teamSource: null,
      entryMethod: null,
      playerUuids: null,
      screenshotMatches: null,
      isReviewingScreenshot: false,
      teamRating: null,
      potentialRating: null,
      topTransfers: [],
      topTransfer: null,
      isSuggestionPending: false,
      playerPortraits: null,
      gw1Performance: null,
      isCalculating: false,
      calculationFailure: null
    }));
  }, [abandonSuggestionPoll]);

  const contextValue: GytrContextValue = {
    state,
    actions: {
      importTeamFromScreenshot: importTeamFromScreenshotAction,
      importTeamByFplId,
      setTeamManually,
      setTeamFromReview,
      setIsReviewingScreenshot,
      calculateRating,
      setCapturedEmail,
      resetImportError,
      resetForRetry
    },
    nextGameweek: nextGameweek ?? 1
  };

  return (
    <GytrContext.Provider value={contextValue}>{children}</GytrContext.Provider>
  );
};

export default GytrProvider;
